import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import { Grid2 } from '@mui/material';
import { AppContext } from 'context/AppContextProvider';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  useCreateIvsDeviceAsync,
  useUpdateIvsDeviceAsync,
} from 'services/Devices/IvsService/hooks/useMutation';
import {
  useCreateLprDeviceAsync,
  useUpdateLprDeviceAsync,
} from 'services/Devices/LprService/hooks/useMutation';
import {
  useCreateRecognitionCameraAsync,
  useUpdateRecognitionCameraAsync,
} from 'services/Devices/Recognition/Cameras/Intelbras/RecognitionCameraService/hooks/useMutation';
import {
  useCreateControlIdDeviceAsync,
  useUpdateControlIdAsync,
} from 'services/Devices/Recognition/Controllers/ControlId/hooks/useMutation';
import {
  useCreateFacialControllerAsync,
  useUpdateFacialControllerAsync,
} from 'services/Devices/Recognition/Controllers/hooks/useMutation';
import {
  useCreateTemperatureDetectorDeviceAsync,
  useUpdateTemperatureDetectorDeviceAsync,
} from 'services/Devices/TemperatureDetectorService/hooks/useMutation';
import { DeviceDiscriminator, IDeviceProps } from 'services/Devices/types';
import zod from 'zod';
import { DeviceFormSchema } from '../../services/Devices/schemas';
import DropdownComponent from '../DropdownComponent';
import DropdownSelectComponent from '../DropdownSelectComponent';
import ModalComponent from '../ModalComponent';
import RadioSelectionComponent from '../RadioSelectionComponent';
import TextFieldComponent from '../TextFieldComponent';
import DeviceDetailsByDiscriminator from 'pages/DevicesPages/_components/DeviceDetailsByDiscriminator';
import {
  useCreateDvrDeviceAsync,
  useUpdateDvrDeviceAsync,
} from '../../services/Devices/DvrService/hooks/useMutation';

type DeviceFormInput = zod.infer<typeof DeviceFormSchema>;

interface DeviceEditionComponentProps {
  isOpen: boolean;
  handleSaveForms: (val: any, reset: () => void) => void;
  brands: any[];
  availableEvents?: any[];
  deviceToEdit?: any;
  setDeviceToEdit?: Dispatch<SetStateAction<any>>;
  isLoading?: boolean;
  discriminator?: DeviceDiscriminator;
  isDvr?: boolean;
  isControllers?: boolean;
  isCam?: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const DeviceEditionComponent = ({
  isOpen,
  setIsOpen,
  handleSaveForms,
  availableEvents,
  deviceToEdit,
  setDeviceToEdit,
  isLoading,
  brands,
  discriminator,
  isControllers,
  isCam,
  isDvr,
}: DeviceEditionComponentProps) => {
  const [deviceTypeSelected, setDeviceTypeSelected] =
    useState<DeviceDiscriminator>();
  const { showSnackbar } = useContext(AppContext);
  const [availableEventsOptions, setAvailableEventsOptions] = useState<any[]>(
    [],
  );
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<DeviceFormInput>({
    resolver: zodResolver(DeviceFormSchema),
    defaultValues: {
      id: undefined,
      availableEvents: [],
      connection: 0,
      brand: 'INTELBRAS',
      password: '',
      channel: 0,
      username: 'admin',
      httpPort: '80',
      name: '',
      model: '',
      tcpPort: '37777',
      rtspPort: '554',
      ip: '',
      serialNumber: '',
      numberOfChannels: 0,
      numberOfAlarmEntries: 0,
      uc: null,
    },
  });

  const {
    mutateAsync: mutateCreateIvsDevice,
    isLoading: isLoadingCreateIvsDevice,
  } = useCreateIvsDeviceAsync();
  const {
    mutateAsync: mutateUpdateIvsDevice,
    isLoading: isLoadingUpdateIvsDevice,
  } = useUpdateIvsDeviceAsync();

  const {
    mutateAsync: mutateCreateLprDevice,
    isLoading: isLoadingCreateLprDevice,
  } = useCreateLprDeviceAsync();
  const {
    mutateAsync: mutateUpdateLprDevice,
    isLoading: isLoadingUpdateLprDevice,
  } = useUpdateLprDeviceAsync();

  const {
    mutateAsync: mutateCreateRecognitionCamera,
    isLoading: isLoadingCreateRecognitionCamera,
  } = useCreateRecognitionCameraAsync();
  const {
    mutateAsync: mutateUpdateRecognitionCamera,
    isLoading: isLoadingUpdateRecognitionCamera,
  } = useUpdateRecognitionCameraAsync();

  const {
    mutateAsync: mutateCreateTemperatureDetectorDevice,
    isLoading: isLoadingCreateTemperatureDetectorDevice,
  } = useCreateTemperatureDetectorDeviceAsync();
  const {
    mutateAsync: mutateUpdateTemperatureDetectorDevice,
    isLoading: isLoadingUpdateTemperatureDetectorDevice,
  } = useUpdateTemperatureDetectorDeviceAsync();

  const {
    mutateAsync: mutateCreateControlIdDevice,
    isLoading: isLoadingCreateControlIdDevice,
  } = useCreateControlIdDeviceAsync();
  const {
    mutateAsync: mutateUpdateControlIdDevice,
    isLoading: isLoadingUpdateControlIdDevice,
  } = useUpdateControlIdAsync();

  const {
    mutateAsync: mutateCreateFacialController,
    isLoading: isLoadingCreateFacialController,
  } = useCreateFacialControllerAsync();
  const {
    mutateAsync: mutateUpdateFacialController,
    isLoading: isLoadingUpdateFacialController,
  } = useUpdateFacialControllerAsync();

  const { mutateAsync: mutateCreateDvr, isLoading: isLoadingCreateDvr } =
    useCreateDvrDeviceAsync();
  const { mutateAsync: mutateUpdateDvr, isLoading: isLoadingUpdateDvr } =
    useUpdateDvrDeviceAsync();

  const handleSaveForm = () => {
    handleSubmit((data) => {
      handleSave(data, reset);
    })();
  };

  const handleSave = async (data: any, reset: () => void) => {
    let result;
    if (data.id) {
      switch (deviceTypeSelected) {
        case 'IvsCameraEntity':
          result = await mutateUpdateIvsDevice({ data, id: data.id });
          break;
        case 'LprCameraEntity':
          result = await mutateUpdateLprDevice({ data, id: data.id });
          break;
        case 'RecognitionCameraEntity':
          result = await mutateUpdateRecognitionCamera({ data, id: data.id });
          break;
        case 'TemperatureDetectorEntity':
          result = await mutateUpdateTemperatureDetectorDevice({
            data,
            id: data.id,
          });
          break;
        case 'ControlIdDeviceEntity':
          result = await mutateUpdateControlIdDevice({
            data,
            id: data.id,
          });
          break;
        case 'FacialControllerEntity':
          result = await mutateUpdateFacialController({
            data,
            id: data.id,
          });
          break;
        case 'DvrEntity':
          result = await mutateUpdateDvr({
            data,
            id: data.id,
          });
          break;
        default:
          return;
      }

      showSnackbar('Dispositivo atualizado com sucesso.', 'success');
      handleSaveForms(data, reset);
      if (setDeviceToEdit) setDeviceToEdit({} as IDeviceProps);
      setDeviceTypeSelected(undefined);
      setIsOpen(false);
      return result;
    }

    switch (deviceTypeSelected) {
      case 'IvsCameraEntity':
        result = await mutateCreateIvsDevice(data);
        break;
      case 'LprCameraEntity':
        result = await mutateCreateLprDevice(data);
        break;
      case 'RecognitionCameraEntity':
        result = await mutateCreateRecognitionCamera(data);
        break;
      case 'TemperatureDetectorEntity':
        result = await mutateCreateTemperatureDetectorDevice(data);
        break;
      case 'ControlIdDeviceEntity':
        result = await mutateCreateControlIdDevice(data);
        break;
      case 'FacialControllerEntity':
        result = await mutateCreateFacialController(data);
        break;
      case 'DvrEntity':
        result = await mutateCreateDvr(data);
        break;
      default:
        return;
    }

    showSnackbar('Dispositivo cadastrado com sucesso.', 'success');
    handleSaveForms(data, reset);
    setDeviceTypeSelected(undefined);
    setIsOpen(false);
    return result;
  };

  const deviceTypeOptions = () => {
    const deviceTypes: {
      label: string;
      value: DeviceDiscriminator;
      type: 'CONTROLLER' | 'CAMERA' | 'DVR';
      isVisible?: boolean;
    }[] = [
      {
        label: 'Reconhecimento Facial',
        value: 'RecognitionCameraEntity',
        type: 'CAMERA',
        isVisible: isCam,
      },
      {
        label: 'LPR',
        value: 'LprCameraEntity',
        type: 'CAMERA',
        isVisible: isCam,
      },
      {
        label: 'IVS',
        value: 'IvsCameraEntity',
        type: 'CAMERA',
        isVisible: isCam,
      },
      {
        label: 'Temperatura',
        value: 'TemperatureDetectorEntity',
        type: 'CAMERA',
        isVisible: isCam,
      },
      {
        label: 'Controlador Control ID',
        value: 'ControlIdDeviceEntity',
        type: 'CONTROLLER',
        isVisible: isControllers,
      },
      {
        label: 'Controlador Comum',
        value: 'FacialControllerEntity',
        type: 'CONTROLLER',
        isVisible: isControllers,
      },
      {
        label: 'DVR/NVR',
        value: 'DvrEntity',
        type: 'DVR',
        isVisible: isDvr,
      },
    ];

    return deviceTypes.filter((item) => item.isVisible);
  };
  const ucOptions = [
    { label: 'Oswaldo Cruz', value: 'UNIDADE_1' },
    { label: 'Pindorama', value: 'UNIDADE_2' },
    { label: 'Fundação Evangélica', value: 'UNIDADE_3' },
  ];
  const connectionOptions = [
    { label: 'Direta', value: 0 },
    { label: 'Auto registro', value: 1 },
  ];

  const onClose = () => {
    reset();
    setIsOpen(false);
    setDeviceTypeSelected(undefined);
    if (setDeviceToEdit) setDeviceToEdit({});
  };

  const watchAvailableEvents = watch('availableEvents');
  const watchConnection = watch('connection');
  const watchBrand = watch('brand');

  const setDefaultType = useCallback(() => {
    if (isDvr && !deviceToEdit?.id) {
      setDeviceTypeSelected('DvrEntity');
    }
  }, [isDvr, deviceToEdit]);

  useEffect(() => {
    if (!watchAvailableEvents) setValue('availableEvents', []);
  }, [watchAvailableEvents]);

  useEffect(() => {
    if (deviceToEdit && deviceToEdit?.id) {
      setDeviceTypeSelected(discriminator);
      reset(deviceToEdit, { keepDefaultValues: true });
    } else {
      reset();
      setDefaultType();
    }
  }, [deviceToEdit, setDefaultType]);

  useEffect(() => {
    if (deviceTypeSelected) {
      setAvailableEventsOptions(
        DeviceDetailsByDiscriminator(deviceTypeSelected)!.availableEvents ?? [],
      );
    }
  }, [deviceTypeSelected]);

  useEffect(() => {
    if (deviceTypeSelected == 'ControlIdDeviceEntity') {
      setValue('brand', 'CONTROLID');
    } else if (deviceTypeSelected == 'FacialControllerEntity' && watchBrand == "CONTROLID")
      setValue('brand', 'INTELBRAS');
  }, [deviceTypeSelected]);

  useEffect(() => {
    if (watchBrand == "TOPDATA") {
      setValue("connection", 1)
      setValue('ip', '')
    }
  },[watchBrand])

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={'Edição do dispositivo'}
    >
      <Grid2
        container
        sx={{ mt: 2 }}
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
      >
        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                label={'Nome'}
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={6}>
          <DropdownComponent
            optionLabel={'label'}
            options={deviceTypeOptions()}
            optionValue="value"
            label="Tipo"
            errorMessage={
              deviceTypeSelected ? undefined : 'Preenchimento obrigatório'
            }
            disabled={deviceToEdit?.id != undefined || isDvr}
            onChange={setDeviceTypeSelected}
            value={deviceTypeSelected}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <DropdownComponent
                options={brands}
                optionLabel={'label'}
                optionValue={'value'}
                value={value}
                onChange={onChange}
                label="Marca"
              />
            )}
            name={'brand'}
            control={control}
          />
        </Grid2>
        <>
          {watchConnection === 0 && (
            <>
              <Grid2 size={6}>
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      value={value}
                      label={'IP'}
                      errorMessage={error?.message}
                      onChangeText={onChange}
                    />
                  )}
                  name={'ip'}
                  control={control}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      value={value}
                      label={'Porta Http'}
                      errorMessage={error?.message}
                      onChangeText={onChange}
                    />
                  )}
                  name={'httpPort'}
                  control={control}
                />
              </Grid2>
              <Grid2 size={6}>
                <Controller
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <TextFieldComponent
                      value={value}
                      label={'Porta de serviço'}
                      errorMessage={error?.message}
                      onChangeText={onChange}
                    />
                  )}
                  name={'tcpPort'}
                  control={control}
                />
              </Grid2>
            </>
          )}

          <Grid2 size={6}>
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  value={value}
                  label={'Porta RTSP'}
                  errorMessage={error?.message}
                  onChangeText={onChange}
                />
              )}
              name={'rtspPort'}
              control={control}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  value={value}
                  label={'Usuário'}
                  errorMessage={error?.message}
                  onChangeText={onChange}
                />
              )}
              name={'username'}
              control={control}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  value={value}
                  label={'Senha'}
                  errorMessage={error?.message}
                  onChangeText={onChange}
                />
              )}
              name={'password'}
              control={control}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <TextFieldComponent
                  value={value}
                  label={'Modelo'}
                  onChangeText={onChange}
                />
              )}
              name={'model'}
              control={control}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name="uc"
              control={control}
              render={({
                         field: { value, onChange },
                         fieldState: { error },
                       }) => (
                <DropdownComponent
                  label="UC (Unidade Corporativa)"
                  value={value}
                  errorMessage={error?.message}
                  optionLabel="label"
                  optionValue="value"
                  options={ucOptions}
                  onChange={onChange}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextFieldComponent
                  value={value}
                  errorMessage={error?.message}
                  label={'Serial'}
                  onChangeText={onChange}
                />
              )}
              name={'serialNumber'}
              control={control}
            />
          </Grid2>

          <Grid2 size={6}>
            {!isDvr ? (
              <Controller
                render={({ field: { value, onChange } }) => (
                  <TextFieldComponent
                    value={value}
                    label={'Canal'}
                    type={'number'}
                    onChangeText={(val) => onChange(Number(val))}
                  />
                )}
                name={'channel'}
                control={control}
              />
            ) : !deviceToEdit?.id ? (
              <Controller
                render={({ field: { value, onChange } }) => (
                  <TextFieldComponent
                    value={value}
                    label={'Número de Canais'}
                    type={'number'}
                    onChangeText={(val) => onChange(Number(val))}
                    min={0}
                  />
                )}
                name={'numberOfChannels'}
                control={control}
              />
            ) : (
              <span />
            )}
          </Grid2>
          {isDvr && !deviceToEdit?.id && (
            <Grid2 size={6}>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <TextFieldComponent
                    value={value}
                    label={'Número de Entradas de Alarme'}
                    type={'number'}
                    onChangeText={(val) => onChange(Number(val))}
                    min={0}
                  />
                )}
                name={'numberOfAlarmEntries'}
                control={control}
              />
            </Grid2>
          )}

          <Grid2 size={6}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <DropdownSelectComponent
                  options={availableEventsOptions}
                  labelField={'label'}
                  valueField={'value'}
                  disabled={!deviceTypeSelected}
                  selectedValues={value ?? []}
                  onSelectedValues={onChange}
                  label="Eventos disponíveis"
                />
              )}
              name={'availableEvents'}
              control={control}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <RadioSelectionComponent
                  options={connectionOptions}
                  value={value}
                  onChange={(val) => onChange(Number(val))}
                  title="Tipo de conexão"
                />
              )}
              name={'connection'}
              control={control}
            />
          </Grid2>
        </>
        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          loading={
            isLoadingCreateIvsDevice ||
            isLoadingCreateLprDevice ||
            isLoadingCreateRecognitionCamera ||
            isLoadingCreateTemperatureDetectorDevice ||
            isLoadingUpdateIvsDevice ||
            isLoadingUpdateLprDevice ||
            isLoadingUpdateRecognitionCamera ||
            isLoadingUpdateTemperatureDetectorDevice ||
            isLoadingCreateFacialController ||
            isLoadingUpdateFacialController ||
            isLoadingCreateControlIdDevice ||
            isLoadingUpdateControlIdDevice ||
            isLoadingCreateDvr ||
            isLoadingUpdateDvr
          }
          fullWidth
          onClick={() => handleSaveForm()}
        >
          Salvar
        </LoadingButton>
      </Grid2>
    </ModalComponent>
  );
};

export default DeviceEditionComponent;
